/**
 * Código estável de uma opção de campo: `^[A-Za-z0-9][A-Za-z0-9_]{0,59}$`, único por campo sem
 * diferenciar caixa. Espelha `CodigoOpcao` do backend.
 *
 * A sugestão local não precisa bater caractere a caractere com `CodigoOpcao.gerar` (Java): o código
 * sugerido é enviado explicitamente e o backend só valida formato e unicidade.
 */
export const TAMANHO_MAXIMO_CODIGO_OPCAO = 60

export const FORMATO_CODIGO_OPCAO = /^[A-Za-z0-9][A-Za-z0-9_]{0,59}$/

export const FORMATO_CODIGO_CAMPO = /^[a-z][a-z0-9_]{0,59}$/

export const formatoValidoOpcao = (codigo: string | null | undefined): boolean =>
  !!codigo && FORMATO_CODIGO_OPCAO.test(codigo)

export const formatoValidoCampo = (codigo: string | null | undefined): boolean =>
  !!codigo && FORMATO_CODIGO_CAMPO.test(codigo)

/** Normalização usada na unicidade e na comparação de códigos de opção. */
export const normalizarCodigoOpcao = (codigo: string | null | undefined): string =>
  codigo == null ? '' : codigo.trim().toLowerCase()

const semAcento = (texto: string): string => texto.normalize('NFD').replace(/[̀-ͯ]/g, '')

const semUnderscoreNasPontas = (texto: string): string =>
  texto.replace(/^_+/, '').replace(/_+$/, '')

const base = (descricao: string | null | undefined): string => {
  const limpo = semUnderscoreNasPontas(
    semAcento(descricao ?? '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
  )
  const naoVazio = limpo === '' ? 'opcao' : limpo
  return naoVazio.slice(0, TAMANHO_MAXIMO_CODIGO_OPCAO)
}

/**
 * Sugere o código de uma opção a partir da descrição, com sufixo numérico em caso de colisão.
 *
 * @param existentes códigos já usados no campo (comparados normalizados)
 */
export const sugerirCodigoOpcao = (
  descricao: string | null | undefined,
  existentes: Array<string | null | undefined> = []
): string => {
  const usados = new Set(existentes.map(normalizarCodigoOpcao).filter((codigo) => codigo !== ''))
  const candidato = base(descricao)
  if (!usados.has(normalizarCodigoOpcao(candidato))) return candidato

  for (let sufixo = 2; sufixo < 1000; sufixo += 1) {
    const marca = `_${sufixo}`
    const truncada = candidato.slice(0, TAMANHO_MAXIMO_CODIGO_OPCAO - marca.length)
    const alternativa = `${truncada}${marca}`
    if (!usados.has(normalizarCodigoOpcao(alternativa))) return alternativa
  }
  return candidato
}
